/**
 * Branded TimePicker — scrolling hour / minute / AM-PM columns in a Radix Popover.
 * Client request (9 Oct 2026): pick times by scrolling, not from a clock face.
 *
 * Drop-in replacement for `<Input type="time">`: same `value` ('HH:MM', 24-hour, or '')
 * and an `onChange(value)` callback, so call sites barely change.
 *
 * e2e: the trigger keeps the field's `data-testid`; each cell exposes
 * `time-hour-{1..12}`, `time-minute-{MM}`, `time-period-{AM|PM}`. A `pickTime`
 * fixture helper drives them from an 'HH:MM' string.
 */
import { useEffect, useRef, useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Clock } from 'lucide-react';
import { cn } from '@/lib/utils';

type Period = 'AM' | 'PM';
type Parts = { hour12: number; minute: number; period: Period };

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1);
const PERIODS: Period[] = ['AM', 'PM'];
const pad = (n: number) => String(n).padStart(2, '0');

function parse(value: string): Parts | null {
  const m = /^(\d{1,2}):(\d{2})/.exec(value);
  if (!m) return null;
  const h = Number(m[1]);
  const minute = Number(m[2]);
  if (h > 23 || minute > 59) return null;
  return { hour12: h % 12 === 0 ? 12 : h % 12, minute, period: h < 12 ? 'AM' : 'PM' };
}

function format({ hour12, minute, period }: Parts): string {
  const h = (hour12 % 12) + (period === 'PM' ? 12 : 0);
  return `${pad(h)}:${pad(minute)}`;
}

function display(value: string): string | null {
  const p = parse(value);
  return p ? `${p.hour12}:${pad(p.minute)} ${p.period}` : null;
}

export interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  /** Minute granularity of the scroll list. A saved value off the step is still listed. */
  minuteStep?: number;
  placeholder?: string;
  id?: string;
  name?: string;
  className?: string;
  disabled?: boolean;
  'data-testid'?: string;
  'aria-label'?: string;
}

export function TimePicker({
  value,
  onChange,
  minuteStep = 5,
  placeholder = 'Select time',
  id,
  name,
  className,
  disabled,
  'data-testid': testId,
  'aria-label': ariaLabel,
}: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const current = parse(value);
  const shown = display(value);

  const minutes = Array.from({ length: Math.ceil(60 / minuteStep) }, (_, i) => i * minuteStep);
  if (current && !minutes.includes(current.minute)) {
    minutes.push(current.minute);
    minutes.sort((a, b) => a - b);
  }

  // First pick from an empty field fills the other columns with a sensible default.
  const base: Parts = current ?? { hour12: 9, minute: 0, period: 'AM' };
  const pick = (patch: Partial<Parts>) => onChange(format({ ...base, ...patch }));

  return (
    <PopoverPrimitive.Root open={open} onOpenChange={setOpen}>
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          id={id}
          name={name}
          disabled={disabled}
          data-testid={testId}
          data-value={value}
          aria-label={ariaLabel}
          data-placeholder={shown ? undefined : ''}
          className={cn(
            'flex h-11 w-full items-center justify-between gap-2 rounded-input border-2 border-input bg-card px-4 text-base text-foreground shadow-[inset_0_2px_4px_rgba(40,23,26,0.04)] transition-all duration-fast',
            'focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary/15 data-[state=open]:border-primary data-[state=open]:ring-4 data-[state=open]:ring-primary/15',
            'disabled:cursor-not-allowed disabled:opacity-50',
            className,
          )}
        >
          <span className={cn('truncate', !shown && 'text-muted-foreground')}>{shown ?? placeholder}</span>
          <Clock className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className={cn(
            'z-50 overflow-hidden rounded-card border-2 border-brand-outline-variant/20 bg-popover p-2 text-popover-foreground shadow-clay-lg',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          )}
        >
          <div className="flex gap-1" data-testid="time-picker-panel">
            <Column
              label="Hour"
              items={HOURS}
              selected={current?.hour12}
              render={(h) => String(h)}
              testid={(h) => `time-hour-${h}`}
              onPick={(hour12) => pick({ hour12 })}
            />
            <Column
              label="Minute"
              items={minutes}
              selected={current?.minute}
              render={pad}
              testid={(m) => `time-minute-${pad(m)}`}
              onPick={(minute) => pick({ minute })}
            />
            <Column
              label="AM/PM"
              items={PERIODS}
              selected={current?.period}
              render={(p) => p}
              testid={(p) => `time-period-${p}`}
              onPick={(period) => pick({ period })}
            />
          </div>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="mt-2 w-full rounded-md bg-primary py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90"
            data-testid="time-picker-done"
          >
            Done
          </button>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}

function Column<T extends string | number>({
  label,
  items,
  selected,
  render,
  testid,
  onPick,
}: {
  label: string;
  items: T[];
  selected: T | undefined;
  render: (item: T) => string;
  testid: (item: T) => string;
  onPick: (item: T) => void;
}) {
  const list_ref = useRef<HTMLDivElement>(null);

  // Bring the selected cell to the middle of its column when the panel opens.
  useEffect(() => {
    const el = list_ref.current?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (el && list_ref.current) {
      list_ref.current.scrollTop = el.offsetTop - list_ref.current.clientHeight / 2 + el.clientHeight / 2;
    }
  }, [selected]);

  return (
    <div className="flex w-16 flex-col">
      <span className="pb-1 text-center text-xs font-medium text-muted-foreground">{label}</span>
      <div
        ref={list_ref}
        role="listbox"
        aria-label={label}
        className="relative h-48 snap-y snap-mandatory overflow-y-auto overscroll-contain rounded-md bg-muted py-[84px] [scrollbar-width:none]"
      >
        {items.map((item) => {
          const is_selected = item === selected;
          return (
            <button
              key={String(item)}
              type="button"
              role="option"
              aria-selected={is_selected}
              data-testid={testid(item)}
              onClick={() => onPick(item)}
              className={cn(
                'block h-8 w-full snap-center rounded-md text-center text-sm tabular-nums transition-colors',
                is_selected ? 'bg-primary font-semibold text-primary-foreground' : 'hover:bg-card',
              )}
            >
              {render(item)}
            </button>
          );
        })}
      </div>
    </div>
  );
}
